/**
 * @module PageBoundaryFallback
 * @package @geenius/adapters/solidjs-css
 * @description Shared page-level error recovery UI for SolidJS CSS adapter pages.
 */

import type { JSX } from "solid-js";
import { cx } from "../lib/cx";

interface PageBoundaryFallbackProps {
  class?: string;
  error: unknown;
  onRetry: () => void;
  title: string;
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  if (typeof error === "string" && error.trim().length > 0) {
    return error;
  }

  return "The adapter page could not finish rendering.";
}

export function PageBoundaryFallback(
  props: PageBoundaryFallbackProps,
): JSX.Element {
  return (
    <div
      class={cx("gn-adapters-page", "gn-adapters-page--narrow", props.class)}
    >
      <div
        class="gn-adapters-alert gn-adapters-alert--error gn-adapters-alert--spaced"
        role="alert"
        aria-live="assertive"
      >
        <strong>{props.title}</strong>
        <p>{getErrorMessage(props.error)}</p>
        <button
          type="button"
          class="gn-adapters-btn gn-adapters-btn--primary"
          onClick={() => props.onRetry()}
        >
          Retry
        </button>
      </div>
    </div>
  );
}
